import { useState } from "react";
import { motion } from "framer-motion";
import {
  HiArrowRight,
  HiCheckCircle,
  HiClock,
  HiLocationMarker,
  HiMail,
  HiPhone,
} from "react-icons/hi";
import SectionTitle from "../components/SectionTitle";
import { contactDetails, contactHighlights, projectTypes } from "../data/content";
import { fadeInUp, stagger } from "../utils/motion";

const initialForm = {
  name: "",
  email: "",
  projectType: projectTypes[0],
  budget: "",
  message: "",
};

const contactIcons = {
  Email: HiMail,
  Phone: HiPhone,
  Location: HiLocationMarker,
};

function ContactSection() {
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setForm(initialForm);
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="relative py-24 sm:py-28">
      <div className="section-shell">
        <SectionTitle
          eyebrow="Contact"
          title="Let's Build Your Next Product"
          subtitle="Tell us what you are planning and our team will help shape it into a high-impact digital solution with clear scope, timeline, and launch support."
        />

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid gap-5 md:grid-cols-3"
        >
          {contactDetails.map((item) => {
            const Icon = contactIcons[item.label];

            return (
              <motion.article
                key={item.label}
                variants={fadeInUp}
                whileHover={{ y: -6, scale: 1.01 }}
                className="glass rounded-2xl p-5 shadow-glass"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyan-200/25 bg-cyan-300/10 text-cyan-200">
                  <Icon className="h-6 w-6" />
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-300 sm:tracking-[0.18em]">
                  {item.label}
                </p>
                <h3 className="mt-2 break-words text-lg font-semibold text-white sm:text-xl">{item.value}</h3>
                <p className="mt-3 text-sm leading-6 text-blue-100/72">{item.text}</p>
              </motion.article>
            );
          })}
        </motion.div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="space-y-6"
          >
            <div className="glass rounded-2xl p-6 shadow-glass sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300 sm:tracking-[0.2em]">
                Project Desk
              </p>
              <h3 className="mt-3 text-2xl font-bold leading-tight text-white sm:text-3xl">
                Share your idea. We will shape the plan.
              </h3>
              <p className="mt-4 text-sm leading-7 text-blue-100/75">
                Whether you need a new platform, a mobile app, a redesign, or help modernizing an
                existing product, BeeVerge can help you define the right roadmap before development
                starts.
              </p>

              <div className="mt-6 space-y-3">
                {contactHighlights.map((highlight) => (
                  <div key={highlight} className="flex gap-3 text-sm leading-6 text-blue-100/82">
                    <HiCheckCircle className="mt-1 h-5 w-5 shrink-0 text-cyan-300" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass rounded-2xl p-6 shadow-glass">
              <div className="flex items-center gap-3">
                <HiClock className="h-6 w-6 text-cyan-300" />
                <h3 className="text-xl font-semibold text-white">Working Hours</h3>
              </div>
              <div className="mt-5 grid gap-3 text-sm text-blue-100/78">
                <div className="flex flex-col gap-1 rounded-lg border border-white/10 bg-black/25 px-4 py-3 min-[420px]:flex-row min-[420px]:justify-between">
                  <span>Monday - Friday</span>
                  <span className="font-semibold text-cyan-200">10:00 AM - 7:00 PM</span>
                </div>
                <div className="flex flex-col gap-1 rounded-lg border border-white/10 bg-black/25 px-4 py-3 min-[420px]:flex-row min-[420px]:justify-between">
                  <span>Saturday</span>
                  <span className="font-semibold text-cyan-200">By appointment</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55 }}
            className="glass rounded-2xl p-6 shadow-glass sm:p-8"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm text-blue-100/85">
                Name
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="mt-2 w-full rounded-lg border border-cyan-200/20 bg-black/35 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-300 focus:shadow-neon"
                  placeholder="Your name"
                />
              </label>

              <label className="text-sm text-blue-100/85">
                Email
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="mt-2 w-full rounded-lg border border-cyan-200/20 bg-black/35 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-300 focus:shadow-neon"
                  placeholder="name@company.com"
                />
              </label>

              <label className="text-sm text-blue-100/85">
                Project Type
                <select
                  name="projectType"
                  value={form.projectType}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-cyan-200/20 bg-black/35 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-300 focus:shadow-neon"
                >
                  {projectTypes.map((type) => (
                    <option key={type} value={type} className="bg-[#070710]">
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm text-blue-100/85">
                Budget
                <input
                  type="text"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-cyan-200/20 bg-black/35 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-300 focus:shadow-neon"
                  placeholder="Example: $5k - $15k"
                />
              </label>
            </div>

            <label className="mt-4 block text-sm text-blue-100/85">
              Message
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                rows={6}
                className="mt-2 w-full resize-none rounded-lg border border-cyan-200/20 bg-black/35 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-300 focus:shadow-neon"
                placeholder="Tell us about goals, features, timeline, and any existing product links"
              />
            </label>

            <motion.button
              type="submit"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
              className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-lg border border-cyan-200/30 bg-cyan-300/10 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-100 transition hover:shadow-neon disabled:cursor-not-allowed disabled:opacity-75 sm:w-auto sm:min-w-[190px] sm:px-6 sm:text-sm sm:tracking-[0.15em]"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-cyan-100 border-t-transparent" />
                  Sending
                </span>
              ) : (
                <>
                  Send Message
                  <HiArrowRight className="h-5 w-5" />
                </>
              )}
            </motion.button>
          </motion.form>
        </div>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          className="glass mt-10 overflow-hidden rounded-2xl p-4 shadow-glass"
        >
          <div className="mb-4 flex flex-col justify-between gap-3 px-2 sm:flex-row sm:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
                Find Us
              </p>
              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">BeeVerge Digital Office</h3>
            </div>
            <p className="text-sm text-blue-100/70">India based, available for global projects</p>
          </div>

          <div className="relative h-[360px] overflow-hidden rounded-xl border border-cyan-200/15">
            <iframe
              title="BeeVerge office map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=72.4310%2C22.9190%2C72.7310%2C23.1190&layer=mapnik&marker=23.0225%2C72.5714"
              className="h-full w-full border-0 grayscale invert"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-cyan-300/5" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ContactSection;
